// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the drafted task panel (design/task-draft-modal, round 4 + 5). A side
// panel on the right on desktop, so the email or chat stays readable, and a
// bottom sheet on the phone. Top to bottom: the source row (what the task is
// made from, Drafted, Redo), a warning when that source already has a task,
// the title and description, one row per field (List with the "next time"
// note, Assign with people photos and one-tap suggestions, Due, Priority),
// then "Why Konvo picked these" (collapsible; opens by itself when no list was
// picked). After Create the panel turns into a done card: Open in Konvo, Open
// in ClickUp, Undo. It closes by itself a few seconds later.
//
// The draft says what the task is; the list, the people and the duplicate
// check come from the server when the panel opens (state/TaskDraft.ts).

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import ChannelStore from '@app/features/channel/state/Channels';
import Activity from '@app/features/konvo_activity/state/Activity';
import KonvoConnections from '@app/features/konvo_connections/state/KonvoConnections';
import {changedTaskFields, reportAssistantUsage} from '@app/features/konvo_connections/state/KonvoUsage';
import {openKonvoTaskInWorkspace} from '@app/features/konvo_tasks/commands/KonvoTaskCommands';
import styles from '@app/features/konvo_tasks/components/draft/TaskDraftPanel.module.css';
import {initialsOf} from '@app/features/konvo_tasks/components/task_sheet/TaskFormat';
import {ListPicker, type SuggestedList} from '@app/features/konvo_tasks/components/workspace/desktop/NewTaskModal';
import {
	AssigneeMenuItems,
	DueMenu,
	membersFor,
	PersonAvatar,
	TagMenu,
} from '@app/features/konvo_tasks/components/workspace/desktop/WorkspaceCells';
import {MenuPopout, menuTokens} from '@app/features/konvo_tasks/components/workspace/desktop/WorkspaceMenu';
import {
	PRIORITY_DESCRIPTORS,
	PriorityFlag,
	SpaceTile,
} from '@app/features/konvo_tasks/components/workspace/shared/WorkspaceBits';
import KonvoTasks, {type KonvoTaskUser} from '@app/features/konvo_tasks/state/KonvoTasks';
import KonvoWorkspace from '@app/features/konvo_tasks/state/KonvoWorkspace';
import {
	fetchTaskDraftContext,
	messageTaskOrigin,
	readWhyOpen,
	scopeDomain,
	type TaskDraftContext,
	type TaskDraftOrigin,
	type TaskListCandidate,
	type TaskSuggestion,
	writeWhyOpen,
} from '@app/features/konvo_tasks/state/TaskDraft';
import {konvoPhotoFor} from '@app/features/konvo_tasks/utils/KonvoPeoplePhotos';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {ChannelMessages} from '@app/features/messaging/state/ChannelMessages';
import KonvoClickUp from '@app/features/messaging/state/KonvoClickUp';
import {messagePreviewText} from '@app/features/messaging/utils/MessagePreviewText';
import {http} from '@app/features/platform/transport/RestTransport';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {KonvoLogo} from '@app/features/ui/components/icons/KonvoLogo';
import {OutlookLogo} from '@app/features/ui/components/icons/OutlookLogo';
import {TeamsLogo} from '@app/features/ui/components/icons/TeamsLogo';
import {WhatsAppLogo} from '@app/features/ui/components/icons/WhatsAppLogo';
import {Spinner} from '@app/features/ui/components/Spinner';
import {
	ArrowCounterClockwiseIcon,
	ArrowsClockwiseIcon,
	CalendarBlankIcon,
	CaretDownIcon,
	CheckIcon,
	FlagIcon,
	ListIcon,
	PaperclipIcon,
	PlusIcon,
	SparkleIcon,
	TagIcon,
	UserIcon,
	WarningCircleIcon,
	XIcon,
} from '@app/features/ui/icons/Icons';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {getCurrentLocale} from '@app/features/user/utils/LocaleUtils';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';

const D = {
	title: msg({message: 'New task', comment: 'Drafted task panel title.'}),
	close: msg({message: 'Close', comment: 'Drafted task panel close button.'}),
	drafted: msg({
		message: 'Drafted',
		comment: 'Drafted task panel: pill on the source row after Konvo filled in the task.',
	}),
	drafting: msg({
		message: 'Drafting…',
		comment: 'Drafted task panel: pill on the source row while Konvo writes the draft.',
	}),
	redo: msg({message: 'Redo', comment: 'Drafted task panel: asks Konvo for a fresh draft from the conversation.'}),
	name: msg({message: 'Task name', comment: 'Drafted task panel: title field placeholder.'}),
	description: msg({message: 'Add a description', comment: 'Drafted task panel: description placeholder.'}),
	list: msg({message: 'List', comment: 'Drafted task panel: field label.'}),
	assign: msg({message: 'Assign', comment: 'Drafted task panel: field label.'}),
	due: msg({message: 'Due', comment: 'Drafted task panel: field label.'}),
	priority: msg({message: 'Priority', comment: 'Drafted task panel: field label.'}),
	chooseList: msg({message: 'Choose a list', comment: 'Drafted task panel: list button when no list is set.'}),
	addPerson: msg({message: 'Add someone', comment: 'Drafted task panel: add assignee button.'}),
	removePerson: msg({message: 'Remove {name}', comment: 'Drafted task panel: remove an assignee.'}),
	suggestPerson: msg({message: 'Assign {name} too', comment: 'Drafted task panel: one-tap suggested assignee.'}),
	today: msg({message: 'Today', comment: 'Drafted task panel: due quick pick.'}),
	tomorrow: msg({message: 'Tomorrow', comment: 'Drafted task panel: due quick pick.'}),
	monday: msg({message: 'Monday', comment: 'Drafted task panel: due quick pick (next Monday).'}),
	nextWeek: msg({message: 'Next week', comment: 'Drafted task panel: due quick pick (a week from today).'}),
	pick: msg({message: 'Pick…', comment: 'Drafted task panel: opens the date picker for the due date.'}),
	clearDue: msg({message: 'Clear due date', comment: 'Drafted task panel.'}),
	attach: msg({message: 'Attach files', comment: 'Drafted task panel footer tool.'}),
	tags: msg({message: 'Tags', comment: 'Drafted task panel footer tool.'}),
	removeFile: msg({message: 'Remove {name}', comment: 'Drafted task panel: remove an attached file.'}),
	cancel: msg({message: 'Cancel', comment: 'Drafted task panel button.'}),
	create: msg({message: 'Create task', comment: 'Drafted task panel button.'}),
	createFailed: msg({
		message: 'Could not create the ClickUp task. Try again.',
		comment: 'Drafted task panel: error toast when creating fails.',
	}),
	needList: msg({message: 'Choose a list for the task.', comment: 'Drafted task panel: validation toast.'}),
	// Source row
	todayAt: msg({
		message: 'today {time}',
		comment: 'Drafted task panel source row: when the email or message came in today.',
	}),
	// Duplicate warning
	existingMail: msg({
		message: 'This email thread already has a task',
		comment: 'Drafted task panel: warning when a task was already made from the same email thread.',
	}),
	existingMessage: msg({
		message: 'This message already has a task',
		comment: 'Drafted task panel: warning when a task was already made from the same message.',
	}),
	existingDetail: msg({
		message: '“{name}” in {list}.',
		comment: 'Drafted task panel duplicate warning: the existing task and its list.',
	}),
	openIt: msg({message: 'Open it', comment: 'Drafted task panel duplicate warning: opens the existing task.'}),
	moveIt: msg({
		message: 'Move it to {list}',
		comment: 'Drafted task panel duplicate warning: moves the existing task.',
	}),
	moved: msg({message: 'Moved to {list}.', comment: 'Toast after moving the existing task to the suggested list.'}),
	moveFailed: msg({
		message: 'Could not move the task. Try again.',
		comment: 'Toast when moving the existing task fails.',
	}),
	// Learning note
	learnDomain: msg({
		message: 'Next time, email from {domain} goes to {list}',
		comment: 'Drafted task panel: shown under the list after the user changed it; Konvo remembers the choice.',
	}),
	learnChat: msg({
		message: 'Next time, tasks from this chat go to {list}',
		comment: 'Drafted task panel: shown under the list after the user changed it; Konvo remembers the choice.',
	}),
	// Why box
	why: msg({
		message: 'Why Konvo picked these',
		comment: 'Drafted task panel: title of the collapsible explanation box.',
	}),
	whyNoList: msg({message: 'No list', comment: 'Drafted task panel: why box summary when no list was picked.'}),
	whyNoDue: msg({message: 'no due date', comment: 'Drafted task panel: why box summary when there is no due date.'}),
	whyYou: msg({
		message: 'you',
		comment: 'Drafted task panel: why box summary when the task is assigned to the reader.',
	}),
	whyYouCap: msg({
		message: 'You',
		comment: 'Drafted task panel: why box value when the task is assigned to the reader.',
	}),
	notPicked: msg({message: 'Not picked', comment: 'Drafted task panel: why box value when no list was picked.'}),
	none: msg({message: 'None', comment: 'Drafted task panel: why box value for an empty field.'}),
	reasonLearnedDomain: msg({
		message: 'You filed the last task from {domain} here',
		comment: 'Drafted task panel: why this list (learned from an earlier choice).',
	}),
	reasonLearnedChat: msg({
		message: 'You filed the last task from this chat here',
		comment: 'Drafted task panel: why this list (learned from an earlier choice).',
	}),
	reasonDomainSender: msg({
		message: '{name} emails from {domain}, which matches {company}',
		comment: "Drafted task panel: why this list (the sender's email domain names the client).",
	}),
	reasonDomain: msg({
		message: '{domain} matches {company}',
		comment: 'Drafted task panel: why this list (an email domain names the client).',
	}),
	reasonChannel: msg({
		message: 'The chat is named like this list',
		comment: 'Drafted task panel: why this list (the chat name matches).',
	}),
	reasonChannelWord: msg({
		message: '{chat} mentions it',
		comment: 'Drafted task panel: a suggested list the chat name mentions.',
	}),
	reasonRecent: msg({message: 'Last used', comment: 'Drafted task panel: a suggested list you used last.'}),
	reasonNothingMail: msg({
		message: 'Nothing in the email points to a list. Maybe:',
		comment: 'Drafted task panel: why box when no list was picked; one-tap maybes follow.',
	}),
	reasonNothingChat: msg({
		message: 'Nothing in the chat points to a list. Maybe:',
		comment: 'Drafted task panel: why box when no list was picked; one-tap maybes follow.',
	}),
	reasonYouChosen: msg({
		message: 'You picked it',
		comment: 'Drafted task panel: why box when the user chose the list themselves.',
	}),
	assignWrote: msg({
		message: '{name} wrote to you',
		comment: 'Drafted task panel: why the reader is the assignee (the email was to them).',
	}),
	assignNamed: msg({
		message: 'Also in the conversation: {names}',
		comment: 'Drafted task panel: people from the email or chat, offered as extra assignees.',
	}),
	assignDraft: msg({
		message: "Konvo's draft names them",
		comment: 'Drafted task panel: why this assignee (the assistant named them).',
	}),
	dueFrom: msg({message: 'Taken from the conversation', comment: 'Drafted task panel: why this due date.'}),
	dueNoneMail: msg({message: 'No date in the email', comment: 'Drafted task panel: why there is no due date.'}),
	dueNoneChat: msg({message: 'No date in the chat', comment: 'Drafted task panel: why there is no due date.'}),
	// Done
	created: msg({message: 'Task created', comment: 'Drafted task panel: done state heading.'}),
	createdIn: msg({message: 'In {path}.', comment: 'Drafted task panel: where the new task went.'}),
	createdLinked: msg({
		message: "In {path}, linked to {name}'s email.",
		comment: 'Drafted task panel: where the new task went, and that it links back to the email.',
	}),
	openKonvo: msg({
		message: 'Open in Konvo',
		comment: 'Drafted task panel done state: opens the task on the Tasks page.',
	}),
	openClickUp: msg({message: 'Open in ClickUp', comment: 'Drafted task panel done state.'}),
	undo: msg({message: 'Undo', comment: 'Drafted task panel done state: deletes the task just made.'}),
	undone: msg({message: 'Task removed.', comment: 'Toast after undoing a task creation.'}),
	undoFailed: msg({
		message: 'Could not undo. Delete the task in ClickUp instead.',
		comment: 'Toast when undoing a task creation fails.',
	}),
	closesSoon: msg({message: 'Closes by itself in a few seconds', comment: 'Drafted task panel done state footer.'}),
	done: msg({message: 'Done', comment: 'Drafted task panel done state: closes the panel.'}),
};

const CONTEXT_RADIUS = 10;
const DONE_CLOSE_MS = 9000;

type Field = 'name' | 'description' | 'list' | 'assignee' | 'due' | 'priority';

export interface TaskDraftPanelProps {
	/** A Konvo message: the draft is written from the chat around it, and Redo asks again. */
	message?: Message | null;
	initialName?: string;
	/** A finished draft: the form opens filled in and skips the assistant run. */
	draft?: TaskSuggestion | null;
	/** What the task is made from; worked out from `message` when not given. */
	origin?: TaskDraftOrigin | null;
	/** Where the panel was opened, for usage tracking. */
	source?: 'strip' | 'menu' | 'mail';
	onClose: () => void;
	onCreated?: (task: {id: string; url: string}) => void;
}

interface CreatedTask {
	id: string;
	url: string;
	name: string;
	listId: string;
	assignees: Array<KonvoTaskUser>;
}

/** The right-clicked message plus up to CONTEXT_RADIUS loaded messages either side, oldest first. */
function gatherContext(message: Message): Array<{author: string; content: string; timestamp: string; target: boolean}> {
	const loaded = ChannelMessages.get(message.channelId)?.toArray() ?? [];
	const index = loaded.findIndex((entry) => entry.id === message.id);
	const window =
		index === -1 ? [message] : loaded.slice(Math.max(0, index - CONTEXT_RADIUS), index + CONTEXT_RADIUS + 1);
	return window
		.filter((entry) => entry.content.trim().length > 0)
		.map((entry) => ({
			author: entry.author.globalName ?? entry.author.username,
			content: entry.content,
			timestamp: entry.timestamp.toISOString(),
			target: entry.id === message.id,
		}));
}

function firstName(name: string | null | undefined): string {
	return (name ?? '').trim().split(/\s+/)[0] ?? '';
}

function whenLabel(i18n: I18n, at: number | null): string {
	if (at == null) return '';
	const date = new Date(at);
	const locale = getCurrentLocale();
	const time = new Intl.DateTimeFormat(locale, {hour: '2-digit', minute: '2-digit'}).format(date);
	const now = new Date();
	if (date.toDateString() === now.toDateString()) return i18n._(D.todayAt, {time});
	return `${new Intl.DateTimeFormat(locale, {day: 'numeric', month: 'short'}).format(date)}, ${time}`;
}

function dueChipLabel(ms: number): string {
	return new Intl.DateTimeFormat(getCurrentLocale(), {weekday: 'short', day: 'numeric', month: 'short'}).format(
		new Date(ms),
	);
}

/** Today 17:00, tomorrow, next Monday, a week from today: the panel's quick due picks. */
function quickDues(now = new Date()): Array<{key: 'today' | 'tomorrow' | 'monday' | 'week'; ms: number}> {
	const at = (days: number) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + days, 17, 0).getTime();
	const toMonday = (8 - now.getDay()) % 7 || 7;
	return [
		{key: 'today', ms: at(0)},
		{key: 'tomorrow', ms: at(1)},
		{key: 'monday', ms: at(toMonday)},
		{key: 'week', ms: at(7)},
	];
}

function memberAsUser(member: {member_id: number; name: string; email: string}): KonvoTaskUser {
	return {
		id: member.member_id,
		username: member.name,
		email: member.email,
		initials: null,
		color: null,
		profile_picture: null,
	};
}

const SourceLogo = ({kind}: {kind: TaskDraftOrigin['kind']}) => {
	switch (kind) {
		case 'mail':
			return <OutlookLogo size={14} />;
		case 'whatsapp':
			return <WhatsAppLogo size={14} />;
		case 'teams':
			return <TeamsLogo size={14} />;
		default:
			return <KonvoLogo size={14} />;
	}
};

const SourcePhoto = observer(({origin}: {origin: TaskDraftOrigin}) => {
	const photo = konvoPhotoFor({name: origin.fromName, email: origin.fromEmail});
	return (
		<span className={styles.sourcePhoto} aria-hidden>
			{photo ? (
				<img src={photo} alt="" />
			) : (
				<span className={styles.sourceInitials}>{initialsOf(origin.fromName ?? origin.title)}</span>
			)}
			<span className={styles.sourceBadge}>
				<SourceLogo kind={origin.kind} />
			</span>
		</span>
	);
});

function listInfo(listId: string | null): {name: string; spaceName: string; spaceColor: string | null} | null {
	if (!listId) return null;
	const node = KonvoWorkspace.findNode('list', listId);
	if (node) return {name: node.name, spaceName: node.space.name, spaceColor: node.space.color ?? null};
	const flat = KonvoClickUp.lists.find((list) => list.id === listId);
	if (!flat) return null;
	const [space = '', ...rest] = flat.path.split(' / ');
	return {name: rest[rest.length - 1] ?? flat.name, spaceName: space, spaceColor: null};
}

export const TaskDraftPanel = observer(
	({message, initialName, draft, origin: givenOrigin, source = 'menu', onClose, onCreated}: TaskDraftPanelProps) => {
		const {i18n} = useLingui();
		const phone = MobileLayout.platformMobileDetected || MobileLayout.enabled;
		const origin = useMemo(() => givenOrigin ?? (message ? messageTaskOrigin(message) : null), [givenOrigin, message]);
		const titleRef = useRef<HTMLTextAreaElement | null>(null);
		const descRef = useRef<HTMLTextAreaElement | null>(null);
		const fileRef = useRef<HTMLInputElement | null>(null);

		// Until a draft arrives, the message itself, with mentions and markup made readable.
		const messageText = useMemo(
			() => (message ? messagePreviewText(message.content, message.guildId ?? null) : ''),
			[message],
		);
		const [name, setName] = useState(() => draft?.name || initialName?.trim() || messageText.slice(0, 120) || '');
		const [description, setDescription] = useState(() => draft?.description ?? (initialName ? '' : messageText));
		const [listId, setListId] = useState<string | null>(draft?.list_id ?? null);
		const [assignees, setAssignees] = useState<Array<KonvoTaskUser>>([]);
		const [due, setDue] = useState<number | null>(draft?.due_date_ms ?? null);
		const [priority, setPriority] = useState<number | null>(draft?.priority ?? null);
		const [tags, setTags] = useState<Array<string>>([]);
		const [files, setFiles] = useState<Array<File>>([]);
		const [suggestion, setSuggestion] = useState<TaskSuggestion | null>(draft ?? null);
		const [suggesting, setSuggesting] = useState(false);
		const [context, setContext] = useState<TaskDraftContext | null>(null);
		const [whyOpen, setWhyOpen] = useState<boolean | null>(null);
		const [busy, setBusy] = useState(false);
		const [created, setCreated] = useState<CreatedTask | null>(null);
		const touched = useRef<Set<Field>>(new Set());
		const suggestRun = useRef(0);
		const markTouched = useCallback((field: Field) => touched.current.add(field), []);

		useEffect(() => {
			KonvoClickUp.prefetch(true);
			void KonvoWorkspace.loadHierarchy();
		}, []);
		useEffect(() => {
			if (listId) void KonvoWorkspace.loadListMeta(listId);
		}, [listId]);

		// ── Server context: list, people, duplicate ─────────────────────────
		useEffect(() => {
			let cancelled = false;
			void fetchTaskDraftContext(origin).then((next) => {
				if (cancelled || !next) return;
				setContext(next);
				if (!touched.current.has('list') && next.list_id) setListId(next.list_id);
			});
			return () => {
				cancelled = true;
			};
		}, [origin]);

		// The reader, or the member the draft names, is assigned once both are known.
		const members = KonvoClickUp.members;
		const me = context?.me ?? (KonvoWorkspace.clickupUserId ? Number(KonvoWorkspace.clickupUserId) : null);
		useEffect(() => {
			if (touched.current.has('assignee')) return;
			const wanted = suggestion?.assignee_id ?? me;
			if (wanted == null) return;
			const member = members.find((entry) => entry.id === wanted);
			if (!member) return;
			setAssignees([memberAsUser({member_id: member.id, name: member.username || member.email, email: member.email})]);
		}, [suggestion?.assignee_id, me, members]);

		// ── Assistant draft (message menu, Redo) ──────────────────────────────
		const applySuggestion = useCallback((next: TaskSuggestion) => {
			const skip = touched.current;
			if (!skip.has('name') && next.name) setName(next.name);
			if (!skip.has('description') && next.description) setDescription(next.description);
			if (!skip.has('list') && next.list_id) setListId(next.list_id);
			if (!skip.has('due') && next.due_date_ms != null) setDue(next.due_date_ms);
			if (!skip.has('priority') && next.priority != null) setPriority(next.priority);
			setSuggestion(next);
		}, []);

		const requestSuggestion = useCallback(async () => {
			if (!message) return;
			const context = gatherContext(message);
			if (!context.some((entry) => entry.target)) return;
			const run = ++suggestRun.current;
			setSuggesting(true);
			try {
				const response = await http.post<{suggestion?: TaskSuggestion}>(Endpoints.KONVO_CLAUDE_SUGGEST_TASK, {
					body: {context, channel_name: ChannelStore.getChannel(message.channelId)?.name ?? null},
					timeoutMs: 90_000,
				});
				if (run !== suggestRun.current) return;
				if (response.body?.suggestion) applySuggestion(response.body.suggestion);
			} catch {
				// Best effort: the panel stays usable without a draft.
			} finally {
				if (run === suggestRun.current) setSuggesting(false);
			}
		}, [message, applySuggestion]);

		useEffect(() => {
			if (!message || draft) return;
			let cancelled = false;
			void (async () => {
				await KonvoConnections.refreshAssistant().catch(() => undefined);
				if (cancelled) return;
				const assistant =
					KonvoConnections.assistantProvider === 'codex' ? KonvoConnections.codex : KonvoConnections.claude;
				if (assistant?.connected && assistant.enabled) void requestSuggestion();
			})();
			return () => {
				cancelled = true;
				suggestRun.current += 1;
			};
		}, [message, draft, requestSuggestion]);

		// Title and description grow with their text. The modal mounts its content after the first
		// layout pass, so the fields are tracked with callback refs, and re-measured when the width,
		// fonts or open animation settle.
		const [titleEl, setTitleEl] = useState<HTMLTextAreaElement | null>(null);
		const [descEl, setDescEl] = useState<HTMLTextAreaElement | null>(null);
		const attachTitle = useCallback((element: HTMLTextAreaElement | null) => {
			titleRef.current = element;
			setTitleEl(element);
		}, []);
		const attachDesc = useCallback((element: HTMLTextAreaElement | null) => {
			descRef.current = element;
			setDescEl(element);
		}, []);
		const fitText = useCallback(() => {
			for (const [element, max] of [
				[titleEl, 120],
				[descEl, 200],
			] as const) {
				if (!element) continue;
				element.style.height = 'auto';
				element.style.height = `${Math.min(element.scrollHeight, max)}px`;
			}
		}, [titleEl, descEl]);
		useLayoutEffect(fitText, [name, description, fitText]);
		useEffect(() => {
			if (!titleEl || typeof ResizeObserver === 'undefined') return;
			let width = titleEl.clientWidth;
			const observer = new ResizeObserver(() => {
				if (titleEl.clientWidth === width) return;
				width = titleEl.clientWidth;
				fitText();
			});
			observer.observe(titleEl);
			void document.fonts?.ready.then(fitText);
			const settle = window.setTimeout(fitText, 400);
			return () => {
				observer.disconnect();
				window.clearTimeout(settle);
			};
		}, [titleEl, fitText]);

		// ── Usage: what the draft said versus what was created ───────────────
		const reported = useRef(false);
		const report = useCallback(
			(wasCreated: boolean) => {
				if (reported.current) return;
				reported.current = true;
				const minute = (ms: number | null) => (ms == null ? null : Math.floor(ms / 60_000));
				const final = {
					name: name.trim(),
					description: description.trim(),
					list_id: listId,
					assignee_id: assignees[0]?.id ?? null,
					due_date_ms: minute(due),
					priority,
				};
				const baseline = suggestion
					? {
							name: suggestion.name,
							description: suggestion.description,
							list_id: context?.list_id ?? suggestion.list_id,
							assignee_id: suggestion.assignee_id,
							due_date_ms: minute(suggestion.due_date_ms),
							priority: suggestion.priority,
						}
					: null;
				reportAssistantUsage({
					kind: 'task_form',
					subject: source,
					data: {from_draft: baseline != null, created: wasCreated, changed: changedTaskFields(baseline, final)},
					...(baseline ? {content: {draft: baseline, final}} : {}),
				});
			},
			[name, description, listId, assignees, due, priority, suggestion, context, source],
		);

		const close = useCallback(() => {
			report(created != null);
			onClose();
		}, [report, onClose, created]);

		// ── Create / Undo ─────────────────────────────────────────────────────
		const submit = async () => {
			const trimmed = name.trim();
			if (!trimmed || busy) {
				titleRef.current?.focus();
				return;
			}
			if (!listId) {
				ToastCommands.createToast({type: 'error', children: i18n._(D.needList)});
				return;
			}
			setBusy(true);
			try {
				const messageLink = message
					? `${window.location.origin}/channels/${message.guildId ?? '@me'}/${message.channelId}/${message.id}`
					: undefined;
				const fullDescription = [description.trim(), origin?.link].filter(Boolean).join('\n\n');
				const response = await http.post<{id: string; url: string}>(Endpoints.KONVO_CLICKUP_TASKS, {
					body: {
						list_id: listId,
						name: trimmed,
						description: fullDescription || undefined,
						assignee_ids: assignees.map((user) => user.id).filter((id): id is number => id != null),
						due_date_ms: due ?? undefined,
						priority: priority ?? undefined,
						tags: tags.length > 0 ? tags : undefined,
						message_link: messageLink,
						source_keys: origin?.sourceKeys ?? [],
						learn_scopes: context?.learn_scopes ?? origin?.scopes ?? [],
					},
				});
				const task = response.body;
				if (!task?.id) throw new Error('no task');
				for (const file of files) await KonvoTasks.uploadAttachment(task.id, file).catch(() => null);
				KonvoWorkspace.noteRecentList(listId);
				Activity.track('action.done', {area: 'tasks', action: 'task.create', origin: 'konvo'});
				void KonvoTasks.refresh();
				onCreated?.(task);
				setCreated({id: task.id, url: task.url, name: trimmed, listId, assignees});
			} catch {
				ToastCommands.createToast({type: 'error', children: i18n._(D.createFailed)});
			} finally {
				setBusy(false);
			}
		};

		const undo = async () => {
			if (!created) return;
			try {
				await http.post(Endpoints.KONVO_CLICKUP_TASK_UNDO_CREATE(created.id));
				ToastCommands.createToast({type: 'success', children: i18n._(D.undone)});
				setCreated(null);
				void KonvoTasks.refresh();
			} catch {
				ToastCommands.createToast({type: 'error', children: i18n._(D.undoFailed)});
			}
		};

		const onKeyDown = (event: React.KeyboardEvent) => {
			if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && !created) {
				event.preventDefault();
				void submit();
			}
		};

		// ── Derived ───────────────────────────────────────────────────────────
		const list = listInfo(listId);
		const kindIsMail = origin?.kind === 'mail';
		const chosenCandidate = context?.candidates.find((entry) => entry.id === listId) ?? null;
		const pickedByKonvo = context?.list_id != null && context.list_id === listId;
		const learnScope = context?.learn_scopes[0];
		const learnDomain = scopeDomain(learnScope);
		const changedByUser = touched.current.has('list') && listId != null && listId !== context?.list_id;
		const assigneeIds = new Set(assignees.map((user) => user.id));
		const suggestedPeople = (context?.people ?? []).filter((person) => !assigneeIds.has(person.member_id));
		const noListPicked = context != null && origin != null && listId == null;
		// The box opens by itself while no list is picked; otherwise it follows the user's last toggle.
		const whyExpanded = whyOpen ?? (noListPicked || readWhyOpen());

		const reasonText = (candidate: TaskListCandidate): string => {
			const reason = candidate.reason;
			switch (reason.kind) {
				case 'learned': {
					const domain = scopeDomain(reason.scope);
					return domain ? i18n._(D.reasonLearnedDomain, {domain}) : i18n._(D.reasonLearnedChat);
				}
				case 'domain': {
					const senderDomain = origin?.fromEmail?.split('@')[1]?.toLowerCase();
					return senderDomain === reason.domain && origin?.fromName
						? i18n._(D.reasonDomainSender, {
								name: firstName(origin.fromName),
								domain: reason.domain,
								company: reason.company,
							})
						: i18n._(D.reasonDomain, {domain: reason.domain, company: reason.company});
				}
				case 'channel':
					return candidate.id === context?.list_id
						? i18n._(D.reasonChannel)
						: i18n._(D.reasonChannelWord, {chat: reason.channel});
				case 'recent':
					return i18n._(D.reasonRecent);
			}
		};

		const suggestedLists: Array<SuggestedList> = (context?.candidates ?? []).map((candidate) => ({
			listId: candidate.id,
			note: reasonText(candidate),
		}));

		const assigneeSummary =
			assignees.length === 0
				? i18n._(D.none)
				: assignees.every((user) => user.id === me)
					? i18n._(D.whyYou)
					: assignees.map((user) => firstName(user.username)).join(', ');
		const dueSummary = due != null ? dueChipLabel(due) : i18n._(D.whyNoDue);
		const whySummary = [list?.name ?? i18n._(D.whyNoList), assigneeSummary, dueSummary].join(' · ');

		const assignReason = (() => {
			const parts: Array<string> = [];
			if (assignees.some((user) => user.id === me) && origin?.fromName && origin.kind !== 'note') {
				parts.push(i18n._(D.assignWrote, {name: firstName(origin.fromName)}));
			} else if (suggestion?.assignee_id != null && suggestion.assignee_id !== me) {
				parts.push(i18n._(D.assignDraft));
			}
			const others = (context?.people ?? []).filter((person) => person.role !== 'sender');
			if (others.length > 0) {
				parts.push(i18n._(D.assignNamed, {names: others.map((person) => firstName(person.name)).join(', ')}));
			}
			return parts.join('. ');
		})();

		const tagSuggestions = [
			...new Set(
				[...KonvoWorkspace.views.values()].flatMap((view) =>
					view.tasks.flatMap((task) => task.tags.map((t) => t.name ?? '')),
				),
			),
		].filter(Boolean);

		// ── Pieces ────────────────────────────────────────────────────────────
		const sourceRow = origin && (
			<div className={styles.source} data-flx="konvo-tasks.task-draft.source">
				<SourcePhoto origin={origin} />
				<div className={styles.sourceText}>
					<b>{origin.title}</b>
					<span>{[origin.fromName, whenLabel(i18n, origin.at)].filter(Boolean).join(' · ')}</span>
				</div>
				{(suggestion || suggesting) && (
					<span className={styles.drafted}>
						{suggesting ? <Spinner size="small" /> : <SparkleIcon size={14} weight="fill" />}
						{i18n._(suggesting ? D.drafting : D.drafted)}
					</span>
				)}
				{message && !suggesting && (
					<button
						type="button"
						className={styles.redo}
						onClick={() => void requestSuggestion()}
						data-flx="konvo-tasks.task-draft.redo"
					>
						<ArrowsClockwiseIcon size={14} />
						{i18n._(D.redo)}
					</button>
				)}
			</div>
		);

		const existing = context?.existing;
		const existingBanner = existing && (
			<div className={styles.existing} role="status" data-flx="konvo-tasks.task-draft.existing">
				<WarningCircleIcon size={18} className={styles.existingIcon} />
				<div className={styles.existingText}>
					<b>{i18n._(kindIsMail ? D.existingMail : D.existingMessage)}</b>
					<span>{i18n._(D.existingDetail, {name: existing.name, list: existing.list_name})}</span>
					<div className={styles.existingActions}>
						<button
							type="button"
							onClick={() => {
								close();
								openKonvoTaskInWorkspace(existing.id, existing.list_id);
							}}
						>
							{i18n._(D.openIt)}
						</button>
						{listId && listId !== existing.list_id && list && (
							<button
								type="button"
								onClick={async () => {
									try {
										await KonvoTasks.updateTask(existing.id, {list_id: listId});
										ToastCommands.createToast({type: 'success', children: i18n._(D.moved, {list: list.name})});
										close();
									} catch {
										ToastCommands.createToast({type: 'error', children: i18n._(D.moveFailed)});
									}
								}}
								data-flx="konvo-tasks.task-draft.move-existing"
							>
								{i18n._(D.moveIt, {list: list.name})}
							</button>
						)}
					</div>
				</div>
			</div>
		);

		const listButton = (
			<Popout
				position="bottom-start"
				offsetMainAxis={8}
				render={({onClose: closePicker}) => (
					<ListPicker
						current={listId}
						suggested={suggestedLists}
						onPick={(next) => {
							markTouched('list');
							setListId(next);
						}}
						close={closePicker}
					/>
				)}
			>
				<button
					type="button"
					className={clsx(styles.listButton, !list && styles.listButtonEmpty)}
					data-flx="konvo-tasks.task-draft.list"
				>
					{list ? (
						<>
							<SpaceTile name={list.spaceName} color={list.spaceColor} size={22} />
							{!phone && <span className={styles.listSpace}>{list.spaceName}</span>}
							{!phone && <span className={styles.listSlash}>/</span>}
							<span className={styles.listName}>{list.name}</span>
						</>
					) : (
						<>
							<ListIcon size={16} />
							<span>{i18n._(D.chooseList)}</span>
						</>
					)}
					<CaretDownIcon size={14} className={styles.caret} />
				</button>
			</Popout>
		);

		const whyBox = origin && context?.connected && (
			<div className={clsx(styles.why, noListPicked && styles.whyUnsure)} data-flx="konvo-tasks.task-draft.why">
				<button
					type="button"
					className={styles.whyHead}
					aria-expanded={whyExpanded}
					onClick={() => {
						setWhyOpen(!whyExpanded);
						writeWhyOpen(!whyExpanded);
					}}
					data-flx="konvo-tasks.task-draft.why-toggle"
				>
					{noListPicked ? <WarningCircleIcon size={15} /> : <SparkleIcon size={15} />}
					<b>{i18n._(D.why)}</b>
					{!whyExpanded && <span className={styles.whySummary}>{whySummary}</span>}
					<CaretDownIcon size={15} className={clsx(styles.whyCaret, whyExpanded && styles.whyCaretOpen)} />
				</button>
				{whyExpanded && (
					<div className={styles.whyRows}>
						<div className={styles.whyRow}>
							<span className={styles.whyKey}>{i18n._(D.list)}</span>
							<span className={styles.whyValue}>
								{list ? <b>{list.name}</b> : <b className={styles.whyNone}>{i18n._(D.notPicked)}</b>}
								{list ? (
									<span>
										{changedByUser
											? i18n._(D.reasonYouChosen)
											: chosenCandidate && pickedByKonvo
												? reasonText(chosenCandidate)
												: i18n._(D.reasonYouChosen)}
									</span>
								) : (
									<>
										<span>{i18n._(kindIsMail ? D.reasonNothingMail : D.reasonNothingChat)}</span>
										<span className={styles.maybes}>
											{(context?.candidates ?? []).map((candidate) => {
												const info = listInfo(candidate.id);
												return (
													<button
														key={candidate.id}
														type="button"
														className={styles.maybe}
														onClick={() => {
															markTouched('list');
															setListId(candidate.id);
														}}
														data-flx="konvo-tasks.task-draft.maybe"
													>
														{info && <SpaceTile name={info.spaceName} color={info.spaceColor} size={18} />}
														{candidate.name}
														{candidate.reason.kind === 'recent' && (
															<span className={styles.maybeNote}>· {i18n._(D.reasonRecent).toLowerCase()}</span>
														)}
													</button>
												);
											})}
										</span>
									</>
								)}
							</span>
						</div>
						<div className={styles.whyRow}>
							<span className={styles.whyKey}>{i18n._(D.assign)}</span>
							<span className={styles.whyValue}>
								<b>
									{assignees.length === 0
										? i18n._(D.none)
										: assignees.every((user) => user.id === me)
											? i18n._(D.whyYouCap)
											: assignees.map((user) => user.username).join(', ')}
								</b>
								{assignReason && <span>{assignReason}</span>}
							</span>
						</div>
						<div className={styles.whyRow}>
							<span className={styles.whyKey}>{i18n._(D.due)}</span>
							<span className={styles.whyValue}>
								{due != null ? <b>{dueChipLabel(due)}</b> : <b className={styles.whyNone}>{i18n._(D.none)}</b>}
								<span>
									{suggestion?.due_date_ms != null
										? i18n._(D.dueFrom)
										: i18n._(kindIsMail ? D.dueNoneMail : D.dueNoneChat)}
								</span>
							</span>
						</div>
					</div>
				)}
			</div>
		);

		const form = (
			<>
				<div className={styles.body}>
					{sourceRow}
					{existingBanner}
					<textarea
						ref={attachTitle}
						className={styles.title}
						rows={1}
						value={name}
						placeholder={i18n._(D.name)}
						aria-label={i18n._(D.name)}
						onChange={(event) => {
							markTouched('name');
							setName(event.target.value.replace(/\n/g, ' '));
						}}
						onKeyDown={(event) => {
							if (event.key === 'Enter' && !event.metaKey && !event.ctrlKey) {
								event.preventDefault();
								descRef.current?.focus();
							}
						}}
						data-flx="konvo-tasks.task-draft.name"
					/>
					<textarea
						ref={attachDesc}
						className={styles.desc}
						rows={2}
						value={description}
						placeholder={i18n._(D.description)}
						aria-label={i18n._(D.description)}
						onChange={(event) => {
							markTouched('description');
							setDescription(event.target.value);
						}}
						data-flx="konvo-tasks.task-draft.description"
					/>
					<div className={styles.rows}>
						<div className={styles.row}>
							<span className={styles.key}>
								<ListIcon size={16} />
								{i18n._(D.list)}
							</span>
							<span className={styles.value}>
								{listButton}
								{changedByUser && learnScope && list && (
									<span className={styles.learn} data-flx="konvo-tasks.task-draft.learn">
										<CheckIcon size={13} weight="bold" />
										{learnDomain
											? i18n._(D.learnDomain, {domain: learnDomain, list: list.name})
											: i18n._(D.learnChat, {list: list.name})}
									</span>
								)}
							</span>
						</div>
						<div className={styles.row}>
							<span className={styles.key}>
								<UserIcon size={16} />
								{i18n._(D.assign)}
							</span>
							<span className={styles.value}>
								{assignees.map((user) => (
									<span key={user.id ?? user.username} className={styles.pill}>
										<PersonAvatar user={user} size={24} />
										<span className={styles.pillName}>{phone ? firstName(user.username) : user.username}</span>
										<button
											type="button"
											className={styles.pillX}
											aria-label={i18n._(D.removePerson, {name: user.username ?? ''})}
											onClick={() => {
												markTouched('assignee');
												setAssignees((current) => current.filter((entry) => entry.id !== user.id));
											}}
										>
											<XIcon size={12} />
										</button>
									</span>
								))}
								<MenuPopout
									wide
									render={() => (
										<AssigneeMenuItems
											members={membersFor(listId)}
											selected={assignees}
											onToggle={(user, on) => {
												markTouched('assignee');
												setAssignees((current) =>
													on ? [...current, user] : current.filter((entry) => entry.id !== user.id),
												);
											}}
										/>
									)}
								>
									<button
										type="button"
										className={styles.addPerson}
										aria-label={i18n._(D.addPerson)}
										title={i18n._(D.addPerson)}
										data-flx="konvo-tasks.task-draft.add-person"
									>
										<PlusIcon size={15} />
									</button>
								</MenuPopout>
								{suggestedPeople.map((person) => {
									const user = memberAsUser(person);
									return (
										<button
											key={person.member_id}
											type="button"
											className={styles.suggestPill}
											title={i18n._(D.suggestPerson, {name: person.name})}
											onClick={() => {
												markTouched('assignee');
												setAssignees((current) => [...current, user]);
											}}
											data-flx="konvo-tasks.task-draft.suggest-person"
										>
											<PersonAvatar user={user} size={24} />
											<span className={styles.pillName}>{firstName(person.name)}</span>
											<PlusIcon size={12} className={styles.suggestPlus} />
										</button>
									);
								})}
							</span>
						</div>
						<div className={styles.row}>
							<span className={styles.key}>
								<CalendarBlankIcon size={16} />
								{i18n._(D.due)}
							</span>
							<span className={styles.value}>
								{due != null ? (
									<span className={clsx(styles.chip, styles.chipOn)}>
										{dueChipLabel(due)}
										<button
											type="button"
											className={styles.pillX}
											aria-label={i18n._(D.clearDue)}
											onClick={() => {
												markTouched('due');
												setDue(null);
											}}
										>
											<XIcon size={12} />
										</button>
									</span>
								) : (
									quickDues()
										.filter((pick) => pick.key !== 'monday' && (!phone || pick.key !== 'week'))
										.map((pick) => (
											<button
												key={pick.key}
												type="button"
												className={styles.chip}
												onClick={() => {
													markTouched('due');
													setDue(pick.ms);
												}}
												data-flx={`konvo-tasks.task-draft.due-${pick.key}`}
											>
												{i18n._(
													pick.key === 'today'
														? D.today
														: pick.key === 'tomorrow'
															? D.tomorrow
															: pick.key === 'monday'
																? D.monday
																: D.nextWeek,
												)}
											</button>
										))
								)}
								<MenuPopout
									render={(closeMenu) => (
										<DueMenu
											value={due}
											onPick={(next) => {
												markTouched('due');
												setDue(next);
											}}
											close={closeMenu}
										/>
									)}
								>
									<button type="button" className={styles.chip} data-flx="konvo-tasks.task-draft.due-pick">
										{i18n._(D.pick)}
									</button>
								</MenuPopout>
							</span>
						</div>
						<div className={clsx(styles.row, styles.rowLast)}>
							<span className={styles.key}>
								<FlagIcon size={16} />
								{i18n._(D.priority)}
							</span>
							<span className={styles.value}>
								<span className={styles.segment} role="radiogroup" aria-label={i18n._(D.priority)}>
									{[1, 2, 3, 4].map((level) => (
										<button
											key={level}
											type="button"
											role="radio"
											aria-checked={priority === level}
											className={clsx(styles.segmentItem, priority === level && styles.segmentOn)}
											onClick={() => {
												markTouched('priority');
												setPriority(priority === level ? null : level);
											}}
											title={i18n._(PRIORITY_DESCRIPTORS[level]!)}
											data-flx={`konvo-tasks.task-draft.priority-${level}`}
										>
											<PriorityFlag priority={level} />
											{!phone && i18n._(PRIORITY_DESCRIPTORS[level]!)}
										</button>
									))}
								</span>
							</span>
						</div>
					</div>
					{(tags.length > 0 || files.length > 0) && (
						<div className={styles.extras}>
							{tags.map((tag) => (
								<span key={tag} className={styles.extra}>
									<TagIcon size={13} />
									{tag}
								</span>
							))}
							{files.map((file, index) => (
								<span key={`${file.name}-${index}`} className={styles.extra}>
									<PaperclipIcon size={13} />
									{file.name}
									<button
										type="button"
										className={styles.pillX}
										aria-label={i18n._(D.removeFile, {name: file.name})}
										onClick={() => setFiles((current) => current.filter((_, at) => at !== index))}
									>
										<XIcon size={12} />
									</button>
								</span>
							))}
						</div>
					)}
					<span className={styles.grow} />
					{whyBox}
				</div>
				<div className={styles.foot}>
					<button
						type="button"
						className={styles.tool}
						aria-label={i18n._(D.attach)}
						title={i18n._(D.attach)}
						onClick={() => fileRef.current?.click()}
					>
						<PaperclipIcon size={19} />
					</button>
					<input
						ref={fileRef}
						type="file"
						multiple
						hidden
						onChange={(event) => {
							const picked = Array.from(event.target.files ?? []);
							setFiles((current) => [...current, ...picked]);
							event.target.value = '';
						}}
					/>
					<MenuPopout
						position="top-start"
						render={() => (
							<TagMenu
								tags={tags}
								suggestions={tagSuggestions}
								onAdd={(tag) => setTags((current) => (current.includes(tag) ? current : [...current, tag]))}
								onRemove={(tag) => setTags((current) => current.filter((entry) => entry !== tag))}
							/>
						)}
					>
						<button type="button" className={styles.tool} aria-label={i18n._(D.tags)} title={i18n._(D.tags)}>
							<TagIcon size={19} />
						</button>
					</MenuPopout>
					<span className={styles.grow} />
					<button type="button" className={styles.cancel} onClick={close} data-flx="konvo-tasks.task-draft.cancel">
						{i18n._(D.cancel)}
					</button>
					<button
						type="button"
						className={styles.create}
						disabled={busy || !name.trim() || !listId}
						onClick={() => void submit()}
						data-flx="konvo-tasks.task-draft.create"
					>
						{busy && <Spinner size="small" />}
						{i18n._(D.create)}
					</button>
				</div>
			</>
		);

		return (
			<Modal.Root
				size="large"
				// The phone's fullscreen modal layer would hold the sheet at the top; the centred one lets it sit at the bottom.
				centered={phone}
				onClose={close}
				initialFocusRef={titleRef}
				className={clsx(styles.root, phone && styles.sheet)}
				data-flx="konvo-tasks.task-draft"
			>
				<Modal.ScreenReaderLabel text={i18n._(D.title)} />
				{/* biome-ignore lint/a11y/noStaticElementInteractions: listens for the ⌘/Ctrl+Enter shortcut from any field inside. */}
				<div className={clsx(menuTokens, styles.panel)} onKeyDown={onKeyDown}>
					{phone && <span className={styles.grabber} aria-hidden />}
					<div className={styles.head}>
						<ClickUpLogo size={20} />
						<b>{i18n._(D.title)}</b>
						<span className={styles.grow} />
						<button
							type="button"
							className={styles.close}
							aria-label={i18n._(D.close)}
							title={i18n._(D.close)}
							onClick={close}
						>
							<XIcon size={20} />
						</button>
					</div>
					{created ? (
						<DoneView created={created} origin={origin} onUndo={() => void undo()} onClose={close} phone={phone} />
					) : (
						form
					)}
				</div>
			</Modal.Root>
		);
	},
);

const DoneView = observer(
	({
		created,
		origin,
		onUndo,
		onClose,
		phone,
	}: {
		created: CreatedTask;
		origin: TaskDraftOrigin | null;
		onUndo: () => void;
		onClose: () => void;
		phone: boolean;
	}) => {
		const {i18n} = useLingui();
		const [paused, setPaused] = useState(false);
		useEffect(() => {
			if (paused) return;
			const timer = window.setTimeout(onClose, DONE_CLOSE_MS);
			return () => window.clearTimeout(timer);
		}, [paused, onClose]);
		const list = listInfo(created.listId);
		const path = list ? `${list.spaceName} / ${list.name}` : '';
		return (
			<div
				className={styles.done}
				onPointerEnter={() => setPaused(true)}
				onFocusCapture={() => setPaused(true)}
				data-flx="konvo-tasks.task-draft.done"
			>
				<div className={styles.doneMain}>
					<span className={styles.doneTick}>
						<CheckIcon size={30} weight="bold" />
					</span>
					<h3>{i18n._(D.created)}</h3>
					<p>
						{origin?.kind === 'mail' && origin.fromName
							? i18n._(D.createdLinked, {path, name: firstName(origin.fromName)})
							: i18n._(D.createdIn, {path})}
					</p>
					<div className={styles.doneCard}>
						<b>{created.name}</b>
						<span className={styles.doneMeta}>
							{created.assignees.slice(0, 3).map((user) => (
								<PersonAvatar key={user.id ?? user.username} user={user} size={20} />
							))}
							{created.assignees[0] && <span>{firstName(created.assignees[0].username)}</span>}
							{list && (
								<>
									<span className={styles.dot} />
									<SpaceTile name={list.spaceName} color={list.spaceColor} size={18} />
									<span>{list.name}</span>
								</>
							)}
						</span>
					</div>
					<div className={clsx(styles.doneActions, phone && styles.doneActionsPhone)}>
						<button
							type="button"
							className={styles.openKonvo}
							onClick={() => {
								onClose();
								openKonvoTaskInWorkspace(created.id, created.listId);
							}}
							data-flx="konvo-tasks.task-draft.open-konvo"
						>
							<KonvoLogo size={20} />
							{i18n._(D.openKonvo)}
						</button>
						{created.url && (
							<a
								className={styles.openClickUp}
								href={created.url}
								target="_blank"
								rel="noreferrer"
								data-flx="konvo-tasks.task-draft.open-clickup"
							>
								<ClickUpLogo size={18} />
								{i18n._(D.openClickUp)}
							</a>
						)}
					</div>
					<button type="button" className={styles.undo} onClick={onUndo} data-flx="konvo-tasks.task-draft.undo">
						<ArrowCounterClockwiseIcon size={14} />
						{i18n._(D.undo)}
					</button>
				</div>
				<div className={styles.foot}>
					{!paused && <span className={styles.closesSoon}>{i18n._(D.closesSoon)}</span>}
					<span className={styles.grow} />
					<button
						type="button"
						className={styles.cancel}
						onClick={onClose}
						data-flx="konvo-tasks.task-draft.done-close"
					>
						{i18n._(D.done)}
					</button>
				</div>
			</div>
		);
	},
);
